import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, BadgeCheck, TrendingUp, Loader2, Sparkles } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, Overline } from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { PARTNER } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Partners() {
  const [form, setForm] = useState({ company: "", name: "", email: "", tier: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.email || !form.name) {
      toast.error("Please add your company, name, and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        package_name: `PARTNER INQUIRY — ${form.company}`,
        message: `[Partner Program]${form.tier ? ` Interested tier: ${form.tier}.` : ""} ${form.message}`,
      });
      setDone(true);
      toast.success("Thanks! Our partnerships team will be in touch within 1 business day.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Partner With Us — Sponsored Content & Featured Placement"
        description="Reach 250K+ high-intent travelers researching travel clubs. Verified listings, sponsored features, and premium partnerships with Travel Club Navigator Group."
        path="/partners"
        image={PARTNER.hero.image}
      />

      {/* HERO */}
      <section className="relative flex min-h-[62vh] items-end overflow-hidden">
        <img
          src={PARTNER.hero.image}
          alt={PARTNER.hero.alt}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="hero-scrim absolute inset-0" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-32 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-white">
              <TrendingUp className="h-4 w-4" /> For Travel Clubs & Resorts
            </span>
            <h1 className="mt-5 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
              Get your brand in front of buyers who are ready to book.
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-200">
              Our readers arrive researching clubs and comparing memberships. Put your story
              in front of them with clearly-labeled, high-converting sponsored content.
            </p>
            <a
              href="#partner-form"
              data-testid="partner-hero-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-4 text-base font-semibold text-primary-foreground btn-glow transition-transform duration-200 hover:scale-105"
            >
              Become a Partner
            </a>
          </motion.div>
        </div>
      </section>

      {/* AUDIENCE STATS */}
      <section className="border-b border-slate-100 bg-white">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 py-12 sm:grid-cols-2 lg:grid-cols-4 lg:px-8">
          {PARTNER.audience.map((a, i) => (
            <Reveal key={a.label} delay={i * 0.06} className="text-center sm:text-left">
              <p className="font-display text-4xl font-black text-primary">{a.stat}</p>
              <p className="mt-1 text-sm text-slate-500">{a.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TIERS */}
      <section className="section-pad mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Overline>Partnership Options</Overline>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
            Choose how you show up.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-8 lg:grid-cols-3">
          {PARTNER.tiers.map((tier, i) => (
            <Reveal key={tier.name} delay={i * 0.08}>
              <article
                data-testid={`partner-tier-${i}`}
                className={`flex h-full flex-col rounded-3xl p-8 ${
                  tier.featured
                    ? "bg-navy text-white ring-2 ring-primary"
                    : "card-3d text-navy"
                }`}
              >
                {tier.featured && (
                  <span className="mb-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                    <Sparkles className="h-3.5 w-3.5" /> Most Popular
                  </span>
                )}
                <h3 className="font-display text-2xl font-bold">{tier.name}</h3>
                <p className={`mt-1 text-sm ${tier.featured ? "text-slate-300" : "text-slate-500"}`}>
                  {tier.desc}
                </p>
                <p className="mt-4 font-display text-2xl font-black text-primary">{tier.price}</p>
                <ul className="mt-6 flex-1 space-y-3">
                  {tier.perks.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm">
                      <Check
                        className={`mt-0.5 h-4.5 w-4.5 flex-none ${
                          tier.featured ? "text-accent" : "text-secondary"
                        }`}
                      />
                      <span className={tier.featured ? "text-slate-200" : "text-slate-600"}>{p}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#partner-form"
                  className={`mt-8 inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold transition-transform duration-200 hover:scale-[1.02] ${
                    tier.featured
                      ? "bg-primary text-primary-foreground btn-glow"
                      : "bg-navy text-white hover:bg-navy/90"
                  }`}
                >
                  Get Started
                </a>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-muted section-pad">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Overline>How It Works</Overline>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
              Live in three simple steps.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {PARTNER.steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="flex h-full gap-5 rounded-3xl bg-white p-8 shadow-sm">
                  <span className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-secondary/10 font-display text-lg font-bold text-secondary">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-bold text-navy">{s.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{s.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.05}>
            <div className="mt-10 flex items-start gap-3 rounded-2xl border border-secondary/20 bg-secondary/5 p-6 text-secondary">
              <BadgeCheck className="mt-0.5 h-6 w-6 flex-none" />
              <p className="text-sm leading-relaxed">
                <strong>Our transparency promise:</strong> every sponsored placement is clearly
                labeled as such. We protect our readers' trust — which is exactly what makes our
                audience so valuable to you.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FORM */}
      <section id="partner-form" className="section-pad mx-auto max-w-3xl px-5 lg:px-8">
        <Reveal>
          <div className="overflow-hidden rounded-[2rem] card-3d">
            <div className="bg-navy px-8 py-8 text-center">
              <h2 className="font-display text-2xl font-bold text-white">Start the conversation</h2>
              <p className="mt-2 text-slate-300">
                Tell us about your club — we'll send a custom media kit within one business day.
              </p>
            </div>
            {done ? (
              <div className="flex flex-col items-center px-8 py-14 text-center">
                <BadgeCheck className="h-16 w-16 text-secondary" />
                <h3 className="mt-5 font-display text-2xl font-bold text-navy">Request received!</h3>
                <p className="mt-2 max-w-sm text-slate-500">
                  Thanks, {form.name.split(" ")[0] || "there"} — our partnerships team will reach out
                  to {form.company} shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4 px-8 py-8">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Company / Club" required>
                    <Input
                      data-testid="partner-company-input"
                      value={form.company}
                      onChange={update("company")}
                      placeholder="Aloha Travelers"
                      className="rounded-xl"
                    />
                  </Field>
                  <Field label="Your name" required>
                    <Input
                      data-testid="partner-name-input"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jordan Rivera"
                      className="rounded-xl"
                    />
                  </Field>
                  <Field label="Work email" required>
                    <Input
                      data-testid="partner-email-input"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@company.com"
                      className="rounded-xl"
                    />
                  </Field>
                  <Field label="Interested tier">
                    <Input
                      data-testid="partner-tier-input"
                      value={form.tier}
                      onChange={update("tier")}
                      placeholder="Sponsored Feature"
                      className="rounded-xl"
                    />
                  </Field>
                </div>
                <Field label="Tell us about your goals">
                  <Textarea
                    data-testid="partner-message-input"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Destinations, audience, campaign goals..."
                    className="min-h-[100px] rounded-xl"
                  />
                </Field>
                <Button
                  type="submit"
                  data-testid="partner-submit-button"
                  disabled={submitting}
                  className="w-full rounded-full bg-primary py-6 text-base font-semibold text-primary-foreground btn-glow hover:bg-primary/90"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
                    </>
                  ) : (
                    "Request Media Kit"
                  )}
                </Button>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </>
  );
}

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-navy">
      {label}
      {required && <span className="text-primary"> *</span>}
    </Label>
    {children}
  </div>
);
